import { Card, Timeline } from 'antd'
import { useTranslation } from 'react-i18next'

import type { SettlementRoot2 } from '@repo/sdk'
import { formatKstDateTime } from '../../utils/formatKstDateTime'

interface SettlementTimelineCardProps {
  settlement: SettlementRoot2
}

function formatTimestamp(timestamp: string): string {
  return formatKstDateTime(timestamp, 'YYYY-MM-DD HH:mm:ss')
}

function createTimelineItem(label: string, timestamp: string, color: string) {
  return {
    color,
    children: (
      <>
        <strong>{label}</strong>
        <br />
        {formatTimestamp(timestamp)}
      </>
    ),
  }
}

function createPendingItem(label: string, color: string, detail?: string | null) {
  return {
    color,
    children: (
      <>
        <strong>{label}</strong>
        {detail && (
          <>
            <br />
            {detail}
          </>
        )}
      </>
    ),
  }
}

export function SettlementTimelineCard({ settlement }: SettlementTimelineCardProps) {
  const { t } = useTranslation()
  const isFailed = settlement.status === 'FAILED'
  const isBlocked = settlement.status === 'BLOCKED'

  const items = [
    createTimelineItem(t('pages.settlements.timeline.created'), settlement.createdAt, 'blue'),
    createTimelineItem(
      t('pages.settlements.timeline.eligible'),
      settlement.eligibilityDate,
      'cyan',
    ),
  ]

  if (settlement.confirmedAt) {
    items.push(
      createTimelineItem(
        t('pages.settlements.timeline.processing'),
        settlement.confirmedAt,
        'purple',
      ),
    )
  } else if (!isFailed && !isBlocked) {
    items.push(createPendingItem(t('pages.settlements.timeline.processing'), 'grey'))
  }

  if (settlement.paidAt) {
    items.push(
      createTimelineItem(t('pages.settlements.timeline.completed'), settlement.paidAt, 'green'),
    )
  } else if (isFailed) {
    items.push(
      createPendingItem(t('pages.settlements.timeline.failed'), 'red', settlement.failureReason),
    )
  } else if (isBlocked) {
    items.push(createPendingItem(t('pages.settlements.timeline.blocked'), 'grey'))
  } else {
    items.push(createPendingItem(t('pages.settlements.timeline.completed'), 'grey'))
  }

  return (
    <Card title={t('pages.settlements.sections.statusTimeline')} bordered>
      <Timeline items={items} />
    </Card>
  )
}
